import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import api from '../utils/api';
import * as LucideIcons from 'lucide-react';
import { 
  Search, 
  X, 
  ArrowRight, 
  ChevronDown, 
  ChevronUp, 
  Building2, 
  Store, 
  Layers, 
  Sparkles 
} from 'lucide-react';
import { getTheme } from '../utils/themes';

const getCategoryIconComponent = (category) => {
  if (!category) return Store;
  const iconName = category.icon;
  if (iconName && LucideIcons[iconName]) {
    return LucideIcons[iconName];
  }
  const slug = (category.slug || category.name || '').toLowerCase();
  if (slug.includes('educat') || slug.includes('school') || slug.includes('college')) return LucideIcons.GraduationCap;
  if (slug.includes('health') || slug.includes('doctor') || slug.includes('hospital') || slug.includes('clinic')) return LucideIcons.HeartPulse;
  if (slug.includes('hotel') || slug.includes('restaurant') || slug.includes('food') || slug.includes('cafe')) return LucideIcons.UtensilsCrossed;
  if (slug.includes('retail') || slug.includes('shop') || slug.includes('store') || slug.includes('mall')) return LucideIcons.ShoppingBag;
  if (slug.includes('construct') || slug.includes('build')) return LucideIcons.HardHat;
  if (slug.includes('agri') || slug.includes('kisan') || slug.includes('farm')) return LucideIcons.Leaf;
  if (slug.includes('real') || slug.includes('estate') || slug.includes('property')) return LucideIcons.Building;
  if (slug.includes('service')) return LucideIcons.Wrench;
  if (slug.includes('auto') || slug.includes('car') || slug.includes('vehicle')) return LucideIcons.Car;
  if (slug.includes('law') || slug.includes('advocate') || slug.includes('legal')) return LucideIcons.Scale;
  if (slug.includes('tech') || slug.includes('it') || slug.includes('software')) return LucideIcons.Laptop;
  if (slug.includes('finance') || slug.includes('bank')) return LucideIcons.Landmark;
  if (slug.includes('cloth') || slug.includes('textile') || slug.includes('wear')) return LucideIcons.Shirt;
  return Store;
};

const AllCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesRes, subcategoriesRes] = await Promise.all([
          api.get('/categories'),
          api.get('/subcategories').catch(() => ({ data: [] }))
        ]);
        setCategories(categoriesRes.data || []);
        setSubcategories(subcategoriesRes.data || []);
      } catch (error) {
        console.error('Error fetching categories and subcategories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Map subcategories grouped by parent category ID
  const subcategoriesByCategory = useMemo(() => {
    const map = new Map();
    subcategories.forEach((sub) => {
      const parentId = sub.categoryId?._id || sub.categoryId;
      if (!parentId) return;
      const key = String(parentId);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(sub);
    });
    return map;
  }, [subcategories]);

  // Total counts for stats
  const totalBusinesses = useMemo(() => {
    return categories.reduce((sum, c) => sum + (c.businessCount || 0), 0);
  }, [categories]);

  // Filter categories and their subcategories based on search
  const filteredCategories = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return categories.map((cat) => ({
        ...cat,
        subs: subcategoriesByCategory.get(String(cat._id)) || []
      }));
    }

    return categories
      .map((cat) => {
        const catNameMatches = cat.name.toLowerCase().includes(query);
        const allSubs = subcategoriesByCategory.get(String(cat._id)) || [];
        const matchingSubs = allSubs.filter((s) => s.name.toLowerCase().includes(query));

        if (catNameMatches || matchingSubs.length > 0) {
          return {
            ...cat,
            subs: catNameMatches ? allSubs : matchingSubs,
            hasMatches: true
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [categories, subcategoriesByCategory, searchTerm]);

  const toggleExpand = (catId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  return (
    <>
      <SEO
        title="All Business Categories & Services in Fatehabad"
        description="Browse all 340+ business categories and services in Fatehabad. Discover verified local shops, doctors, automotive, education, and professionals."
        canonical="/categories"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
        {/* Hero Banner Header */}
        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white overflow-hidden py-12 md:py-16 shadow-inner">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
          
          <div className="container-custom relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-4 text-xs md:text-sm text-primary-100">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="opacity-60">/</span>
              <span className="text-white font-medium">All Categories</span>
            </div>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-medium mb-3 text-primary-100">
                <Sparkles className="w-3.5 h-3.5 text-yellow-300" /> Complete Business Directory Taxonomy
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-3">
                All Categories & Services
              </h1>
              <p className="text-sm md:text-base text-primary-100 leading-relaxed max-w-2xl">
                Explore verified businesses, shops, and professionals in Fatehabad district organized across {categories.length} main categories and {subcategories.length}+ specialized subcategories.
              </p>
            </div>

            {/* Quick Stats Counter */}
            <div className="grid grid-cols-3 gap-3 md:gap-6 mt-8 max-w-xl">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 md:p-4 border border-white/15 text-center">
                <div className="text-xl md:text-3xl font-black text-white">{categories.length}</div>
                <div className="text-xs text-primary-200 font-medium mt-0.5">Main Categories</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 md:p-4 border border-white/15 text-center">
                <div className="text-xl md:text-3xl font-black text-white">{subcategories.length}</div>
                <div className="text-xs text-primary-200 font-medium mt-0.5">Subcategories</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 md:p-4 border border-white/15 text-center">
                <div className="text-xl md:text-3xl font-black text-white">
                  {totalBusinesses ? totalBusinesses.toLocaleString() : '10,700+'}
                </div>
                <div className="text-xs text-primary-200 font-medium mt-0.5">Live Listings</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Sticky Bar */}
        <div className="sticky top-16 z-30 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-sm py-4">
          <div className="container-custom">
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search any category or subcategory (e.g. Auto Parts, Hospital, Boutique, School)..."
                className="w-full pl-12 pr-10 py-3 text-sm md:text-base bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all shadow-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3.5 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="container-custom py-10 md:py-14">
          {loading ? (
            <PageLoader message="Loading categories and subcategories..." />
          ) : filteredCategories.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 text-center border border-gray-200 dark:border-gray-800 shadow-sm max-w-md mx-auto my-12">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4 text-gray-400">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                No matching categories found
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                No category or subcategory matched "{searchTerm}". Try a different keyword.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
              >
                Clear Search Filter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {filteredCategories.map((category) => {
                const IconComponent = getCategoryIconComponent(category);
                const theme = getTheme(category.theme || 'blue');
                const subs = category.subs || [];
                const isExpanded = expandedCategories[category._id];
                const hasMatches = searchTerm && searchTerm.trim().length > 0;
                const shouldShowSubs = isExpanded || hasMatches;

                return (
                  <div
                    key={category._id}
                    className="group flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-xs hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200 border border-gray-100 dark:border-gray-800 overflow-hidden"
                  >
                    {/* Compact Main Row (Exact visual style of BusinessCard) */}
                    <div
                      className="flex flex-row items-center w-full cursor-pointer pr-3"
                      onClick={() => navigate(`/${category.slug}`)}
                    >
                      {/* Icon Section */}
                      <div
                        className="relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl m-2.5 sm:m-3 my-auto flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                        style={{
                          backgroundColor: `${theme.colors.primary}15`,
                          color: theme.colors.primary
                        }}
                      >
                        <IconComponent className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={1.75} />
                      </div>

                      {/* Content Body */}
                      <div className="flex-1 flex flex-col justify-center min-w-0 py-2.5 pr-2 pl-0.5">
                        <h3 className="text-[13px] sm:text-sm md:text-[15px] font-bold text-gray-900 dark:text-white line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors leading-snug">
                          {category.name}
                        </h3>

                        {/* Subcategories Text Preview */}
                        <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 line-clamp-1 mt-0.5">
                          {subs.length > 0
                            ? subs.map((s) => s.name).join(', ')
                            : 'Verified local business listings'}
                        </p>

                        {/* Badges / Stats line */}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {category.businessCount > 0 ? (
                            <span className="text-[10px] sm:text-[11px] font-bold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/60 px-2 py-0.5 rounded-md border border-primary-200/50 dark:border-primary-900/50">
                              {category.businessCount.toLocaleString()} Listings
                            </span>
                          ) : (
                            <span className="text-[10px] sm:text-[11px] font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded-md">
                              Direct Category
                            </span>
                          )}

                          {subs.length > 0 && (
                            <span className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                              {subs.length} Subcategories
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: Subcategory Toggle & Explore Action */}
                      <div
                        className="flex items-center gap-1 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {subs.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleExpand(category._id)}
                            className={`p-1.5 sm:p-2 rounded-xl transition-colors ${
                              isExpanded
                                ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                            title={isExpanded ? 'Hide subcategories' : 'View subcategories'}
                            aria-label="Toggle subcategories"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        <Link
                          to={`/${category.slug}`}
                          className="p-1.5 sm:p-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-400 group-hover:text-white group-hover:bg-primary-600 dark:group-hover:bg-primary-600 transition-all"
                          title={`Explore ${category.name}`}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>

                    {/* Expandable Subcategories Chips Drawer */}
                    {shouldShowSubs && subs.length > 0 && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800/80 bg-gray-50/60 dark:bg-gray-900/40">
                        <div className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 mb-1.5 pt-1.5 flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Layers className="w-3 h-3 text-primary-500" /> All Subcategories ({subs.length})
                          </span>
                          <Link
                            to={`/${category.slug}`}
                            className="text-primary-600 dark:text-primary-400 hover:underline font-bold"
                          >
                            View All →
                          </Link>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {subs.map((sub) => {
                            const isMatched = searchTerm && sub.name.toLowerCase().includes(searchTerm.toLowerCase());
                            return (
                              <Link
                                key={sub._id}
                                to={`/${category.slug}?subcategory=${sub.slug}`}
                                className={`text-[11px] px-2.5 py-1 rounded-lg font-medium transition-all ${
                                  isMatched
                                    ? 'bg-amber-100 dark:bg-amber-950/70 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700 font-bold'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-600 dark:hover:text-primary-300 border border-gray-200/70 dark:border-gray-700/80 shadow-2xs'
                                }`}
                              >
                                {sub.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllCategories;
